"use client";

import { Button } from "../ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../ui/form";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { useIntlForm } from "@erp/shared-libs/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { requiredStringWithMaxSchema } from "@erp/shared-libs/common";
import { Separator } from "../ui/separator";
import { CheckableSubmitButton } from "../forms/checkable-submit-button";

const MAX_AUDIT_REASON_LENGTH = 300;

interface AuditReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void | Promise<void>;
  /**
   * 送出前要讓使用者知道的一行說明（例：這次儲存的影響）；沒給時不顯示，畫面和原本完全相同
   */
  notice?: string;
}

export const AuditReasonDialog: FC<AuditReasonDialogProps> = ({ open, onOpenChange, ...props }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="w-11/12 max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <AuditReasonDialogContent onOpenChange={onOpenChange} {...props} />
      </DialogContent>
    </Dialog>
  );
};

const AuditReasonDialogContent: FC<Omit<AuditReasonDialogProps, "open">> = ({
  onOpenChange,
  onConfirm,
  notice,
}) => {
  const { t } = useTranslation(["common"]);

  const form = useIntlForm<{ auditReaseon: string }>({
    mode: "onChange",
    resolver: zodResolver(
      z.object({
        auditReaseon: requiredStringWithMaxSchema(MAX_AUDIT_REASON_LENGTH),
      })
    ),
    reValidateMode: "onChange",
    defaultValues: {
      auditReaseon: undefined,
    },
  });

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      form.handleSubmit(async (data) => {
        await onConfirm(data.auditReaseon);
        onOpenChange(false);
      })(event);
    },
    [form, onConfirm, onOpenChange]
  );

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex flex-col pt-4">
          <DialogHeader className="px-4">
            <DialogTitle asChild>
              <div className="text-lg font-bold">{t("common:dialog.audit_reason.title")}</div>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground mb-2.5 text-sm">
              {t("common:dialog.audit_reason.message")}
            </DialogDescription>
          </DialogHeader>
          <Separator className="" />
          <div className="bg-background whitespace-pre-wrap px-4 py-6 text-sm">
            {notice != null && (
              <p data-audit-reason-notice className="text-muted-foreground-darker mb-3">
                {notice}
              </p>
            )}
            <FormField
              control={form.control}
              name="auditReaseon"
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <FormItem className="grid grid-cols-1 items-center">
                    <div className="col-span-full sm:col-span-10">
                      <FormControl>
                        <Textarea
                          placeholder={t("common:dialog.audit_reason.placeholder.reason")}
                          type="text"
                          className="h-36"
                          maxLength={MAX_AUDIT_REASON_LENGTH}
                          {...field}
                          value={field.value || ""}
                          disabled={form.formState.isSubmitting}
                        />
                      </FormControl>
                    </div>
                  </FormItem>

                  <FormMessage className="ml-auto" />
                </div>
              )}
            />
          </div>
        </div>
        <Separator />
        <div className="flex items-end justify-end px-4 py-2">
          <Button variant="outline" size="sm" className="px-8" onClick={() => onOpenChange(false)}>
            {t("common:button.cancel")}
          </Button>
          <CheckableSubmitButton
            targetForm={form}
            disabled={!form.formState.isValid || form.formState.isSubmitting}
          >
            {t("common:button.submit")}
          </CheckableSubmitButton>
        </div>
      </form>
    </Form>
  );
};
